import { GET_USER_CRM_SETTINGS } from "@/api";
import { useQuery } from "@tanstack/react-query";
import { useSessionContext } from "@/context";
import { InstalledIntegrationType } from "@/types/installedintegration";
import { useMemo } from "react";
import { getCRMSettingsByType } from "@/api/client/crm-settings";
import { CrmType } from "@/types/crm-settings";
import { useIntegrationsPage } from "./useIntegrationsPage";
import { FeatureFlagName } from "@/types/feature-flag";
import {
  getIsContactSyncEnabled,
  getParagonPushSettings,
} from "@/lib/paragon-config";
import { ParagonPushSettings } from "@/types/paragon-config";

export const useCrm = (): {
  userSettings: Awaited<ReturnType<typeof getCRMSettingsByType>> | undefined;
  isConnected: boolean | undefined;
  isLoading: boolean;
  crmType: CrmType | undefined;
  pushSettings: ParagonPushSettings | null;
  isContactSyncEnabled: boolean;
} => {
  const { userId, getFeatureFlag } = useSessionContext();
  const { installedIntegrations, isIntegrationsLoading, paragonConfigs } =
    useIntegrationsPage({
      userId,
      getFeatureFlag,
    });

  const paragonSalesforceEnabled =
    getFeatureFlag(FeatureFlagName.PARAGON_SALESFORCE).value === "on";

  const {
    salesforce,
    wealthbox,
    redtail,
    paragonMicrosoftDynamics,
    paragonZoho,
    paragonCloven,
    paragonSalesforce,
    paragonXlr8,
    paragonPractifi,
    paragonBlackDiamondElements,
    paragonFsc,
    paragonMaximizer,
    paragonEquisoft,
    laylah,
  } = useMemo(() => {
    const salesforce = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.SALESFORCE,
    );
    const wealthbox = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.WEALTHBOX,
    );

    // ParagonConfig existence = integration enabled (firm admin setup)
    // InstalledIntegration with externalId = user connected (user has individual connection)
    const paragonZoho = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_ZOHO,
    );
    const paragonCloven = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_CLOVEN,
    );

    const redtail = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.REDTAIL,
    );
    const paragonMicrosoftDynamics = installedIntegrations?.find(
      (i) =>
        i.integration === InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS,
    );
    const paragonSalesforce = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_SALESFORCE,
    );
    const paragonXlr8 = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_XLR8,
    );
    const paragonPractifi = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_PRACTIFI,
    );
    const paragonBlackDiamondElements = installedIntegrations?.find(
      (i) =>
        i.integration ===
        InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS,
    );
    const paragonFsc = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_FSC,
    );
    const paragonMaximizer = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_MAXIMIZER,
    );
    const paragonEquisoft = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_EQUISOFT,
    );
    const laylah = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.LAYLAH,
    );

    return {
      salesforce,
      wealthbox,
      redtail,
      paragonMicrosoftDynamics,
      paragonZoho,
      paragonCloven,
      paragonSalesforce,
      paragonXlr8,
      paragonPractifi,
      paragonBlackDiamondElements,
      paragonFsc,
      paragonMaximizer,
      paragonEquisoft,
      laylah,
    };
  }, [installedIntegrations]);

  const crmType = useMemo(() => {
    if (wealthbox) return CrmType.WEALTHBOX;
    if (salesforce) return CrmType.SALESFORCE;
    if (redtail) return CrmType.REDTAIL;
    if (paragonZoho) return CrmType.PARAGON_ZOHO;
    if (paragonCloven) return CrmType.PARAGON_CLOVEN;
    if (paragonMicrosoftDynamics) return CrmType.PARAGON_MICROSOFTDYNAMICS;
    if (paragonSalesforceEnabled && paragonSalesforce)
      return CrmType.PARAGON_SALESFORCE;
    if (paragonSalesforceEnabled && paragonXlr8) return CrmType.PARAGON_XLR8;
    if (paragonSalesforceEnabled && paragonPractifi)
      return CrmType.PARAGON_PRACTIFI;
    if (paragonSalesforceEnabled && paragonBlackDiamondElements)
      return CrmType.PARAGON_SS_C_BLACKDIAMONDELEMENTS;
    if (paragonSalesforceEnabled && paragonFsc) return CrmType.PARAGON_FSC;
    if (paragonMaximizer) return CrmType.PARAGON_MAXIMIZER;
    if (paragonEquisoft) return CrmType.PARAGON_EQUISOFT;
    if (laylah) return CrmType.LAYLAH;
    return;
  }, [
    salesforce,
    wealthbox,
    redtail,
    paragonMicrosoftDynamics,
    paragonZoho,
    paragonCloven,
    paragonSalesforce,
    paragonXlr8,
    paragonPractifi,
    paragonBlackDiamondElements,
    paragonFsc,
    paragonSalesforceEnabled,
    paragonMaximizer,
    paragonEquisoft,
    laylah,
  ]);

  const { data: userSettings, isLoading: isSettingsLoading } = useQuery({
    queryKey: [GET_USER_CRM_SETTINGS],
    queryFn: () => getCRMSettingsByType(),
    enabled: !!crmType,
  });

  // Get Paragon push settings for Microsoft Dynamics
  const pushSettings = useMemo(() => {
    if (
      crmType !== CrmType.PARAGON_MICROSOFTDYNAMICS &&
      crmType !== CrmType.PARAGON_ZOHO &&
      crmType !== CrmType.PARAGON_CLOVEN &&
      crmType !== CrmType.PARAGON_SALESFORCE &&
      crmType !== CrmType.PARAGON_XLR8 &&
      crmType !== CrmType.PARAGON_PRACTIFI &&
      crmType !== CrmType.PARAGON_SS_C_BLACKDIAMONDELEMENTS &&
      crmType !== CrmType.PARAGON_FSC &&
      crmType !== CrmType.PARAGON_MAXIMIZER &&
      crmType !== CrmType.PARAGON_EQUISOFT
    ) {
      return null;
    }
    if (crmType === CrmType.PARAGON_ZOHO) {
      return getParagonPushSettings(
        paragonConfigs,
        InstalledIntegrationType.PARAGON_ZOHO,
      );
    } else if (crmType === CrmType.PARAGON_CLOVEN) {
      return getParagonPushSettings(
        paragonConfigs,
        InstalledIntegrationType.PARAGON_CLOVEN,
      );
    } else if (crmType === CrmType.PARAGON_MICROSOFTDYNAMICS) {
      return getParagonPushSettings(
        paragonConfigs,
        InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS,
      );
    } else if (crmType === CrmType.PARAGON_SALESFORCE) {
      return getParagonPushSettings(
        paragonConfigs,
        InstalledIntegrationType.PARAGON_SALESFORCE,
      );
    } else if (crmType === CrmType.PARAGON_XLR8) {
      return getParagonPushSettings(
        paragonConfigs,
        InstalledIntegrationType.PARAGON_XLR8,
      );
    } else if (crmType === CrmType.PARAGON_PRACTIFI) {
      return getParagonPushSettings(
        paragonConfigs,
        InstalledIntegrationType.PARAGON_PRACTIFI,
      );
    } else if (crmType === CrmType.PARAGON_SS_C_BLACKDIAMONDELEMENTS) {
      return getParagonPushSettings(
        paragonConfigs,
        InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS,
      );
    } else if (crmType === CrmType.PARAGON_FSC) {
      return getParagonPushSettings(
        paragonConfigs,
        InstalledIntegrationType.PARAGON_FSC,
      );
    } else if (crmType === CrmType.PARAGON_MAXIMIZER) {
      return getParagonPushSettings(
        paragonConfigs,
        InstalledIntegrationType.PARAGON_MAXIMIZER,
      );
    } else if (crmType === CrmType.PARAGON_EQUISOFT) {
      return getParagonPushSettings(
        paragonConfigs,
        InstalledIntegrationType.PARAGON_EQUISOFT,
      );
    }
    return null;
  }, [crmType, paragonConfigs]);

  // Get contact sync enabled status from Paragon config
  // Check firm-level ParagonConfig regardless of whether user has connected
  const isContactSyncEnabled = useMemo(() => {
    const paragonIntegrationTypes = [
      InstalledIntegrationType.PARAGON_ZOHO,
      InstalledIntegrationType.PARAGON_CLOVEN,
      InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS,
      InstalledIntegrationType.PARAGON_MAXIMIZER,
      InstalledIntegrationType.PARAGON_SALESFORCE,
      InstalledIntegrationType.PARAGON_XLR8,
      InstalledIntegrationType.PARAGON_PRACTIFI,
      InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS,
      InstalledIntegrationType.PARAGON_FSC,
      InstalledIntegrationType.PARAGON_EQUISOFT,
    ];

    // Scope contact-sync evaluation to the active crmType
    const matchedType = paragonIntegrationTypes.find(
      (type) => (type as string) === (crmType as string),
    );
    if (matchedType) {
      return getIsContactSyncEnabled(paragonConfigs, matchedType);
    }

    // For non-Paragon CRMs, fall back to userSettings.autoUpdate
    if (crmType) {
      return userSettings?.autoUpdate ?? true;
    }

    // No user CRM connection — check if firm admin has configured any Paragon CRM with contact sync
    if (paragonConfigs?.length) {
      return paragonConfigs.some(
        (config) =>
          paragonIntegrationTypes.includes(config.integration) &&
          getIsContactSyncEnabled(paragonConfigs, config.integration),
      );
    }

    // No CRM configured at any level
    return false;
  }, [crmType, paragonConfigs, userSettings?.autoUpdate]);

  // Check if any CRM is connected
  // ParagonConfig existence = integration enabled (firm admin setup)
  // InstalledIntegration with externalId = user connected (user has individual connection)
  const isConnected = useMemo(() => {
    // Check for non-Paragon CRMs
    const hasNonParagonCrm = installedIntegrations?.some(
      (i) =>
        i.integration === InstalledIntegrationType.SALESFORCE ||
        i.integration === InstalledIntegrationType.WEALTHBOX ||
        i.integration === InstalledIntegrationType.REDTAIL ||
        i.integration === InstalledIntegrationType.LAYLAH,
    );

    // Check for Paragon CRMs - only check installedIntegrations (user-level connections)
    const hasParagonMicrosoftDynamics = installedIntegrations?.some(
      (i) =>
        i.integration === InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS,
    );

    const hasParagonSalesforce =
      paragonSalesforceEnabled &&
      installedIntegrations?.some(
        (i) =>
          i.integration === InstalledIntegrationType.PARAGON_SALESFORCE ||
          i.integration === InstalledIntegrationType.PARAGON_XLR8 ||
          i.integration === InstalledIntegrationType.PARAGON_PRACTIFI ||
          i.integration ===
            InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS ||
          i.integration === InstalledIntegrationType.PARAGON_FSC,
      );

    const hasParagonZoho = installedIntegrations?.some(
      (i) => i.integration === InstalledIntegrationType.PARAGON_ZOHO,
    );

    const hasParagonCloven = installedIntegrations?.some(
      (i) => i.integration === InstalledIntegrationType.PARAGON_CLOVEN,
    );

    const hasParagonMaximizer = installedIntegrations?.some(
      (i) => i.integration === InstalledIntegrationType.PARAGON_MAXIMIZER,
    );

    const hasParagonEquisoft = installedIntegrations?.some(
      (i) => i.integration === InstalledIntegrationType.PARAGON_EQUISOFT,
    );

    return !!(
      hasNonParagonCrm ||
      hasParagonMicrosoftDynamics ||
      hasParagonSalesforce ||
      hasParagonZoho ||
      hasParagonCloven ||
      hasParagonMaximizer ||
      hasParagonEquisoft
    );
  }, [installedIntegrations, paragonSalesforceEnabled]);

  return {
    userSettings,
    isConnected,
    isLoading: isSettingsLoading || isIntegrationsLoading,
    crmType,
    pushSettings,
    isContactSyncEnabled,
  };
};
